import { useEffect, useState } from 'react';
import {
  listAddresses,
  setDefaultAddress,
  type ApiClient,
  type ShippingAddress,
} from '../../../../modules/api';

export interface DeliveryAddressesState {
  /** Saved shipping addresses, default first. */
  addresses: ShippingAddress[];
  /** Currently chosen address (the default is preselected on load). */
  selected: ShippingAddress | null;
  /** True while the initial list is loading. */
  loading: boolean;
  /** Pick a saved address for this shipment AND persist it as the player's
   *  default (web parity: pick = make default). */
  select: (address: ShippingAddress) => void;
  /** Register a freshly created address: the backend makes it the default, so
   *  mirror that locally and select it. */
  addCreated: (address: ShippingAddress) => void;
}

/**
 * Owns the New-delivery address state: loads the player's saved addresses on
 * mount, preselects their default, and exposes pick / add-created actions.
 * Picking persists the default (PUT /addresses/:id/default) optimistically.
 */
export function useDeliveryAddresses(client: ApiClient): DeliveryAddressesState {
  const [addresses, setAddresses] = useState<ShippingAddress[]>([]);
  const [selected, setSelected] = useState<ShippingAddress | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void listAddresses(client)
      .then((list) => {
        if (cancelled) return;
        setAddresses(list);
        setSelected(list.find((a) => a.isDefault) ?? list[0] ?? null);
      })
      .catch(() => {
        /* leave empty → the user creates the first address */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  const select = (address: ShippingAddress) => {
    // Picking also makes it the default (web parity), so the selected copy must
    // carry isDefault: true — otherwise the "Your Address" summary renders the
    // just-picked address without its Default badge until a reload.
    const picked = { ...address, isDefault: true };
    setSelected(picked);
    // Optimistically move the default flag to the picked row; persist server-side.
    setAddresses((list) => list.map((a) => ({ ...a, isDefault: a.id === address.id })));
    void setDefaultAddress(client, address.id).catch(() => {
      /* keep the optimistic state if the persist fails */
    });
  };

  const addCreated = (address: ShippingAddress) => {
    const fresh = { ...address, isDefault: true };
    setAddresses((list) => [
      fresh,
      ...list.filter((a) => a.id !== address.id).map((a) => ({ ...a, isDefault: false })),
    ]);
    setSelected(fresh);
  };

  return { addresses, selected, loading, select, addCreated };
}
